/** @velyn-custom BEGIN — vely-newsletter-popup.js
 *  Self-contained promo popup (Figma "Popup Banner Desktop").
 *  Trigger / cookie behavior mirrors core section-newsletter-popup.js,
 *  with optional merchant overrides — see sections/vely-newsletter-popup.liquid
 *  schema for every setting referenced below. All new behavior defaults to
 *  reproducing the exact prior behavior (flat 1-day decline cooldown, no
 *  badge, no exclusions) until a merchant opts in.
 *
 *  data-show-on-every-refresh="true"
 *  - Ignores dismiss/success/reject cookies so each page load can auto-open again.
 *  - Does not write those cookies (so testing / refresh mode stays reliable).
 *  - Still opens at most ONCE per page load (triggers torn down after open).
 *
 *  data-show-on-every-refresh="false" (default, core-like):
 *  - Success subscribe → cookie 365 days.
 *  - Decline → tiered cooldown (1st/2nd/"from then on" hours, all merchant-set).
 *  - Backdrop / Escape → its own separate, merchant-set cooldown (0 = none, core-like).
 *
 *  Auto-open triggers:
 *  - "both": scroll % first, then inactivity timer (core sequence).
 *  - "scroll" / "inactivity": as labeled in the section schema.
 *  - "exit_intent": opens once when the mouse leaves toward the top of the
 *    viewport (desktop pointer devices only — never fires on touch, so it
 *    naturally does nothing on mobile).
 *
 *  Sticky badge: when enabled and the popup is in a decline cooldown, a
 *  small persistent tab (own close button) lets the visitor reopen the
 *  offer without the popup forcing itself back open.
 */
import * as NextSkyTheme from "@NextSkyTheme/global";

const HIDDEN_COOKIE = "vely_newsletter_popup_hidden"; // legacy flat cookie, still honored if present
const SUCCESS_COOKIE = "vely_newsletter_popup_success";
const REJECT_COUNT_COOKIE = "vely_newsletter_popup_reject_count";
const REJECT_UNTIL_COOKIE = "vely_newsletter_popup_reject_until";
const BACKDROP_UNTIL_COOKIE = "vely_newsletter_popup_backdrop_until";
const BADGE_DISMISSED_COOKIE = "vely_newsletter_popup_badge_dismissed";

const SUCCESS_DAYS = 365;
const REJECT_COUNT_DAYS = 400; // long-lived counter — a year+ so it doesn't quietly reset
const MOBILE_BREAKPOINT = 767.98; // matches theme convention, .cursor/rules/06-responsive-breakpoints.mdc

class VelyNewsletterPopup extends HTMLElement {
  connectedCallback() {
    if (this._isReady) return;
    this._isReady = true;

    this.enable = this.dataset.enable;
    this.triggerMode = this.dataset.triggerMode || "both";
    this.scrollPercent = parseFloat(this.dataset.scrollPercent) || 70;
    this.inactivitySeconds = parseFloat(this.dataset.inactivitySeconds) || 4;
    this.showOnEveryRefresh = this.dataset.showOnEveryRefresh === "true";

    this.rejectCooldown1Hours = parseFloat(this.dataset.rejectCooldown1Hours) || 24;
    this.rejectCooldown2Hours = parseFloat(this.dataset.rejectCooldown2Hours) || 24;
    this.showAfterSecondReject = this.dataset.showAfterSecondReject === "true";
    this.rejectCooldownFinalHours = parseFloat(this.dataset.rejectCooldownFinalHours) || 24;
    this.backdropCooldownMinutes = parseFloat(this.dataset.backdropCooldownMinutes) || 0;
    this.disableOnMobile = this.dataset.disableOnMobile === "true";
    this.enableStickyBadge = this.dataset.enableStickyBadge === "true";

    this.modal = this.querySelector(".vely-newsletter-popup__modal");
    this.dialog = this.querySelector(".vely-newsletter-popup__dialog");
    this.form = this.querySelector(".vely-newsletter-popup__form");
    this.badge = this.querySelector("[data-vely-newsletter-badge]");
    this.badgeOpenButton = this.querySelector("[data-vely-newsletter-badge-open]");
    this.badgeCloseButton = this.querySelector("[data-vely-newsletter-badge-close]");

    this._scrollTriggered = false;
    this._inactivityTriggered = false;
    this._autoOpenDone = false;
    this._inactivityTimer = null;
    this._activeTrigger = null;

    this.onDocumentClick = this.onDocumentClick.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
    this.onScroll = this.onScroll.bind(this);
    this.onExitIntent = this.onExitIntent.bind(this);
    this.resetInactivityTimer = this.resetInactivityTimer.bind(this);

    document.addEventListener("click", this.onDocumentClick);
    document.addEventListener("keydown", this.onKeydown);
    this.bindFormSuccessCookie();
    this.bindBadge();
    this.refreshBadgeVisibility();

    if (this.isDesignMode()) return;
    if (!this.isPopupPageEligible()) return;
    if (this.isMobileDisabled()) return;
    if (this.shouldBlockPopupAutoTrigger()) return;

    this.initTriggers();
  }

  disconnectedCallback() {
    document.removeEventListener("click", this.onDocumentClick);
    document.removeEventListener("keydown", this.onKeydown);
    this.teardownTriggers();
    this.closeDialog();
    this._isReady = false;
  }

  isDesignMode() {
    return this.dataset.designMode === "true" || Boolean(window.Shopify?.designMode);
  }

  isPopupPageEligible() {
    return this.enable === "show-on-homepage" || this.enable === "show-all-page";
  }

  isMobileDisabled() {
    return this.disableOnMobile && window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT}px)`).matches;
  }

  /** When refresh-mode is on, cookies never block a new page load. */
  shouldBlockPopupAutoTrigger() {
    if (this.showOnEveryRefresh) return false;
    return (
      this.hasSuccessCookie() ||
      this.hasLegacyHiddenCookie() ||
      this.isWithinRejectCooldown() ||
      this.isWithinBackdropCooldown()
    );
  }

  shouldPersistCookies() {
    return !this.showOnEveryRefresh && !this.isDesignMode();
  }

  hasSuccessCookie() {
    return NextSkyTheme.getCookie(SUCCESS_COOKIE) !== null;
  }

  /** Older sessions may still carry the pre-tiered flat cookie — keep honoring it until it expires naturally. */
  hasLegacyHiddenCookie() {
    return NextSkyTheme.getCookie(HIDDEN_COOKIE) !== null;
  }

  isWithinRejectCooldown() {
    return this.readFutureTimestamp(REJECT_UNTIL_COOKIE) > Date.now();
  }

  isWithinBackdropCooldown() {
    return this.readFutureTimestamp(BACKDROP_UNTIL_COOKIE) > Date.now();
  }

  readFutureTimestamp(cookieName) {
    const raw = NextSkyTheme.getCookie(cookieName);
    const parsed = raw ? parseInt(raw, 10) : 0;
    return Number.isFinite(parsed) ? parsed : 0;
  }

  getRejectCount() {
    const raw = NextSkyTheme.getCookie(REJECT_COUNT_COOKIE);
    const parsed = raw ? parseInt(raw, 10) : 0;
    return Number.isFinite(parsed) ? parsed : 0;
  }

  getScrollMetrics() {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const documentHeight = Math.max(
      0,
      document.documentElement.scrollHeight - document.documentElement.clientHeight
    );
    const scrollPercentage =
      documentHeight > 0 ? (scrollTop / documentHeight) * 100 : 100;
    return { scrollTop, documentHeight, scrollPercentage };
  }

  canReachScrollTrigger() {
    return this.getScrollMetrics().documentHeight > 40;
  }

  initTriggers() {
    if (this._autoOpenDone || this.shouldBlockPopupAutoTrigger()) return;

    const mode = this.triggerMode;
    const canScroll = this.canReachScrollTrigger();

    if (mode === "exit_intent") {
      this.initExitIntentTrigger();
      return;
    }

    if (mode === "inactivity") {
      this.initInactivityTrigger();
      return;
    }

    if (mode === "scroll") {
      if (!canScroll) {
        this.openDialog();
        return;
      }
      window.addEventListener("scroll", this.onScroll, { passive: true });
      this.onScroll();
      return;
    }

    // "both" — core: scroll gate, then inactivity.
    if (!canScroll) {
      this.initInactivityTrigger();
      return;
    }
    window.addEventListener("scroll", this.onScroll, { passive: true });
    this.onScroll();
  }

  teardownTriggers() {
    window.removeEventListener("scroll", this.onScroll);
    document.removeEventListener("mouseleave", this.onExitIntent);
    this.clearInactivityListeners();
    this.clearInactivityTimer();
  }

  initExitIntentTrigger() {
    // mouseleave only fires for real pointer devices leaving the viewport
    // (never on touch), so this naturally does nothing on mobile/tablet.
    document.addEventListener("mouseleave", this.onExitIntent);
  }

  onExitIntent(event) {
    if (this._autoOpenDone) return;
    // Only trigger when the cursor exits through the TOP edge — leaving via
    // the sides/bottom is normal scrolling/interaction, not "about to leave".
    if (event.clientY > 0) return;
    document.removeEventListener("mouseleave", this.onExitIntent);
    this.openDialog();
  }

  onScroll() {
    if (this._autoOpenDone || this._scrollTriggered) return;
    if (this.modal?.classList.contains("is-open")) return;

    const { scrollPercentage } = this.getScrollMetrics();
    if (scrollPercentage < this.scrollPercent) return;

    this._scrollTriggered = true;
    window.removeEventListener("scroll", this.onScroll);

    if (this.triggerMode === "scroll") {
      this.openDialog();
      return;
    }

    this.initInactivityTrigger();
  }

  initInactivityTrigger() {
    if (this._inactivityEvents || this._inactivityTriggered || this._autoOpenDone) {
      return;
    }

    this._inactivityEvents = ["scroll", "click", "mousemove", "keydown"];
    this._inactivityEvents.forEach((eventName) => {
      window.addEventListener(eventName, this.resetInactivityTimer, { passive: true });
    });
    this.resetInactivityTimer();
  }

  clearInactivityListeners() {
    if (!this._inactivityEvents) return;
    this._inactivityEvents.forEach((eventName) => {
      window.removeEventListener(eventName, this.resetInactivityTimer);
    });
    this._inactivityEvents = null;
  }

  resetInactivityTimer() {
    if (this._autoOpenDone || this._inactivityTriggered) return;
    if (this.modal?.classList.contains("is-open")) return;

    this.clearInactivityTimer();
    this._inactivityTimer = window.setTimeout(() => {
      if (this._autoOpenDone || this._inactivityTriggered) return;
      this._inactivityTriggered = true;
      this.clearInactivityListeners();
      this.clearInactivityTimer();
      this.openDialog();
    }, this.inactivitySeconds * 1000);
  }

  clearInactivityTimer() {
    if (this._inactivityTimer) {
      window.clearTimeout(this._inactivityTimer);
      this._inactivityTimer = null;
    }
  }

  onDocumentClick(event) {
    if (!this.modal?.classList.contains("is-open")) return;

    const closeTrigger = event.target.closest("[data-vely-newsletter-close]");
    if (closeTrigger && this.contains(closeTrigger)) {
      event.preventDefault();
      const isDecline = closeTrigger.hasAttribute("data-vely-newsletter-decline");
      if (isDecline) {
        this.handleDecline();
      } else {
        this.handleBackdropClose();
      }
    }
  }

  onKeydown(event) {
    if (!this.modal?.classList.contains("is-open")) return;

    if (event.key === "Escape") {
      event.preventDefault();
      this.handleBackdropClose();
      return;
    }

    if (event.key === "Tab") {
      this.trapFocus(event);
    }
  }

  /** Explicit "no thanks" — tiered cooldown + reject counter + optional sticky badge. */
  handleDecline() {
    if (this.shouldPersistCookies()) {
      const nextCount = this.getRejectCount() + 1;
      const cooldownHours = this.computeRejectCooldownHours(nextCount);
      // null → "stop showing entirely" (merchant turned off 'show again
      // after 2nd decline'): use a ~100-year duration AND a matching real
      // cookie lifetime, so this reliably stays "in cooldown" for as long
      // as practically matters, instead of quietly expiring.
      const isPermanent = cooldownHours === null;
      const effectiveHours = isPermanent ? 24 * 365 * 100 : cooldownHours;
      const cookieDays = isPermanent ? 365 * 100 : REJECT_COUNT_DAYS;

      NextSkyTheme.setCookie(REJECT_COUNT_COOKIE, String(nextCount), cookieDays);
      const until = Date.now() + effectiveHours * 60 * 60 * 1000;
      NextSkyTheme.setCookie(REJECT_UNTIL_COOKIE, String(until), cookieDays);
    }

    this.closeDialog(); // cookie handled above, with the tiered logic
    this.refreshBadgeVisibility();
  }

  /** 1st decline → tier 1, 2nd → tier 2, 3rd+ → final tier (or "never again" if disabled). */
  computeRejectCooldownHours(rejectCount) {
    if (rejectCount <= 1) return this.rejectCooldown1Hours;
    if (rejectCount === 2) return this.rejectCooldown2Hours;
    if (!this.showAfterSecondReject) return null; // caller stores a permanent stop instead
    return this.rejectCooldownFinalHours;
  }

  /** Backdrop click / Escape — separate, usually-shorter cooldown, no reject counter, no badge. */
  handleBackdropClose() {
    if (this.shouldPersistCookies() && this.backdropCooldownMinutes > 0) {
      const until = Date.now() + this.backdropCooldownMinutes * 60 * 1000;
      NextSkyTheme.setCookie(BACKDROP_UNTIL_COOKIE, String(until), 1);
    }
    this.closeDialog();
  }

  /** manual: true bypasses the auto-trigger/cooldown guards — used by the
   *  sticky badge's own "reopen" button (and any other explicit, user-
   *  initiated open) so a visitor can still get back to the form during an
   *  active decline cooldown. Auto-triggers (scroll/inactivity/exit-intent)
   *  always call this with no arguments and keep respecting the cooldown. */
  openDialog({ manual = false } = {}) {
    if (!this.modal || this.modal.classList.contains("is-open")) return;
    if (!manual) {
      if (this._autoOpenDone) return;
      if (this.shouldBlockPopupAutoTrigger()) return;
    }

    this._autoOpenDone = true;
    this._inactivityTriggered = true;
    this.teardownTriggers();

    this._activeTrigger = document.activeElement;

    this.modal.classList.add("is-open");
    document.documentElement.classList.add("vely-newsletter-popup-open");
    this.modal.setAttribute("aria-hidden", "false");
    this.hideBadge();

    const focusTarget =
      this.dialog?.querySelector(".vely-newsletter-popup__input") || this.dialog;
    focusTarget?.focus({ preventScroll: true });
  }

  /** Cookie-writing now lives entirely in handleDecline()/handleBackdropClose() —
   *  this just closes the dialog UI. */
  closeDialog() {
    if (!this.modal || !this.modal.classList.contains("is-open")) return;

    this._autoOpenDone = true;
    this.teardownTriggers();

    this.modal.classList.remove("is-open");
    document.documentElement.classList.remove("vely-newsletter-popup-open");
    this.modal.setAttribute("aria-hidden", "true");

    if (this._activeTrigger && document.contains(this._activeTrigger)) {
      this._activeTrigger.focus?.({ preventScroll: true });
    }
  }

  trapFocus(event) {
    if (!this.dialog) return;

    const focusableElements = this.dialog.querySelectorAll(
      'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])'
    );
    if (!focusableElements.length) return;

    const first = focusableElements[0];
    const last = focusableElements[focusableElements.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus({ preventScroll: true });
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus({ preventScroll: true });
    }
  }

  bindFormSuccessCookie() {
    if (!this.form || this.form.dataset.bound === "true") return;
    this.form.dataset.bound = "true";

    // Native {% form 'customer' %} submit reloads the page. Shopify appends
    // ?customer_posted=true on success — set the success cookie then.
    // (Toast is shown by modal.js checkUrlParameters.)
    if (
      !this.isDesignMode() &&
      /[?&]customer_posted=true(?:&|$)/.test(window.location.search)
    ) {
      if (this.shouldPersistCookies()) {
        NextSkyTheme.setCookie(SUCCESS_COOKIE, "true", SUCCESS_DAYS);
      }
    }
  }

  /* ---------------------------------------------------------------------
   * Sticky badge (opt-in): appears once a decline cooldown is active, lets
   * the visitor reopen the offer on their own terms, has its own close
   * button that only hides the badge (does not touch the reject cooldown).
   * ------------------------------------------------------------------- */

  bindBadge() {
    if (!this.enableStickyBadge || !this.badge) return;

    if (this.badgeOpenButton) {
      this.badgeOpenButton.addEventListener("click", () => {
        this.hideBadge();
        this.openDialog({ manual: true }); // bypass cooldown — explicit user action
      });
    }

    if (this.badgeCloseButton) {
      this.badgeCloseButton.addEventListener("click", () => {
        if (this.shouldPersistCookies()) {
          NextSkyTheme.setCookie(BADGE_DISMISSED_COOKIE, "true", 1);
        }
        this.hideBadge();
      });
    }
  }

  shouldShowStickyBadge() {
    if (!this.enableStickyBadge || !this.badge) return false;
    if (this.isDesignMode()) return false;
    if (this.isMobileDisabled()) return false; // 'Disable on mobile' hides the badge too, not just auto-triggers
    if (this.hasSuccessCookie()) return false;
    if (NextSkyTheme.getCookie(BADGE_DISMISSED_COOKIE) !== null) return false;
    // Only show while an actual decline cooldown is in effect — not before
    // the visitor has ever declined, and not once they've subscribed.
    return this.getRejectCount() > 0 && this.isWithinRejectCooldown();
  }

  refreshBadgeVisibility() {
    if (!this.badge) return;
    this.badge.hidden = !this.shouldShowStickyBadge();
  }

  hideBadge() {
    if (this.badge) this.badge.hidden = true;
  }
}

if (!customElements.get("vely-newsletter-popup")) {
  customElements.define("vely-newsletter-popup", VelyNewsletterPopup);
}
/** @velyn-custom END — vely-newsletter-popup.js */
